// Backend client + job polling + login gate.
// 401 semantics: any 401 other than /api/login = expired session → onUnauthorized callback → App returns to login.
// A 401 from /api/login = wrong password; that is a form-level error and must not trigger the global redirect.

import type { CanvasState, ChatMsg, JobRecord, TurnProgress, TurnRecord } from './types';

let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(fn: () => void): void {
  unauthorizedHandler = fn;
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const resp = await fetch(path, init);
  if (resp.status === 401 && !path.startsWith('/api/login')) {
    unauthorizedHandler?.();
  }
  return resp;
}

/** True when a thrown fetch error is just an AbortController cancellation (SPEC-392 loop cancel). */
export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

// ===== Auth =====

// ===== Cross-device cloud sync =====
// The cloud is only a carrier: pull on startup, debounced push on change; local localStorage stays the primary store.
// SPEC-454 M1: the pull is three-state — a FAILED pull must never look like an empty cloud (2026-09-28
// incident: a client whose GET failed booted seed-only and overwrote the whole history). Only an explicit
// {doc:null} counts as "cloud empty"; network/5xx/timeout/parse failure is 'error' → cloud sync pauses
// (no cloudReady, no push; local editing + localStorage continue) and retries with exponential backoff.

/** Three-state pull result (SPEC-454 M1). 'empty' = the server explicitly returned {doc:null} — the only
 * form that counts as an empty cloud. 'error' = any failure — never initialize/overwrite from it. */
export type FetchDocResult =
  | { status: 'doc'; doc: Record<string, unknown> }
  | { status: 'empty' }
  | { status: 'error'; reason: string };

export async function fetchDoc(): Promise<FetchDocResult> {
  let resp: Response;
  try {
    resp = await apiFetch('/api/doc');
  } catch {
    return { status: 'error', reason: 'network' };
  }
  if (!resp.ok) return { status: 'error', reason: `http ${resp.status}` };
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    return { status: 'error', reason: 'bad json' };
  }
  if (!body || typeof body !== 'object' || Array.isArray(body)) return { status: 'error', reason: 'bad shape' };
  // Empty-store placeholder: server returns {doc: null}
  if ('doc' in body && (body as { doc: unknown }).doc === null) return { status: 'empty' };
  return { status: 'doc', doc: body as Record<string, unknown> };
}

/** SPEC-454 M1 backoff schedule: 5s → 15s → 45s (interval ×3), capped at 120s. */
export const RETRY_BASE_MS = 5_000;
export const RETRY_CAP_MS = 120_000;
export function retryDelayMs(attempt: number): number {
  return Math.min(RETRY_BASE_MS * 3 ** Math.max(0, attempt), RETRY_CAP_MS);
}

/**
 * SPEC-454 M1 — pull with exponential-backoff retry. Only 'doc'/'empty' end the loop; 'error' keeps
 * retrying (5s/15s/45s… cap 120s) so one transient failure can no longer silently disable cloud sync
 * for the whole session. Deps are injectable for tests. Returns null when alive() flips false
 * (logout/unmount) — the caller must NOT treat that as a pull result (no cloudReady, no push).
 */
export async function pullDocWithRetry(deps: {
  fetchOnce: () => Promise<FetchDocResult>;
  sleep: (ms: number) => Promise<void>;
  alive: () => boolean;
  onError?: (attempt: number, delayMs: number) => void;
}): Promise<FetchDocResult | null> {
  for (let attempt = 0; ; attempt++) {
    if (!deps.alive()) return null;
    const res = await deps.fetchOnce();
    if (!deps.alive()) return null;
    if (res.status !== 'error') return res;
    const delay = retryDelayMs(attempt);
    deps.onError?.(attempt, delay);
    await deps.sleep(delay);
  }
}

/** SPEC-454 M3: PUT /api/doc result. The server union-merges the payload with the stored doc
 * (per-project LWW + tombstones) and reports the outcome — merged = "written result ≠ payload"
 * (strictly same-source on the server), revoked = project ids the merge dropped. merged:true
 * makes the client re-pull once to converge; merged:false skips it (no KV read amplification).
 * SPEC-469 M2: status = 失败响应的 HTTP 状态码（增量可选字段，既有消费方/测试不破；网络异常
 * catch 分支不带 status = 现状语义）。409 = 服务端终审门确定性拒绝 — 重试无用，须如实上报。 */
export interface PutDocResult { ok: boolean; merged: boolean; revoked: string[]; status?: number }

export async function putDoc(docJson: string): Promise<PutDocResult> {
  const failed: PutDocResult = { ok: false, merged: false, revoked: [] };
  try {
    const resp = await apiFetch('/api/doc', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: docJson,
    });
    if (!resp.ok) return { ...failed, status: resp.status }; // SPEC-469 M2: 409 与断网从此可区分
    const body = (await resp.json().catch(() => null)) as { merged?: unknown; revoked?: unknown } | null;
    const revokedRaw = body && Array.isArray(body.revoked) ? body.revoked : [];
    return {
      ok: true,
      merged: body?.merged === true,
      revoked: revokedRaw.filter((x): x is string => typeof x === 'string'),
    };
  } catch {
    return failed;
  }
}

export async function fetchMe(): Promise<{ username: string; role: string } | null> {
  // Startup session check uses bare fetch: a 401 here only means "not logged in", not "session expired" —
  // going through apiFetch would fire onUnauthorized and the login page would wrongly show "session expired".
  try {
    const resp = await fetch('/api/me');
    if (!resp.ok) return null;
    const j = await resp.json();
    return { username: String(j?.username ?? ''), role: String(j?.role ?? 'learner') };
  } catch {
    return null;
  }
}

export type LoginResult = 'ok' | 'bad_credentials' | 'not_configured' | 'network_error';

export async function postLogin(username: string, password: string): Promise<LoginResult> {
  let resp: Response;
  try {
    resp = await fetch('/api/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    return 'network_error';
  }
  if (resp.ok) return 'ok';
  if (resp.status === 503) return 'not_configured';
  if (resp.status === 401) return 'bad_credentials';
  return 'network_error';
}

export async function postLogout(): Promise<void> {
  try {
    await apiFetch('/api/logout', { method: 'POST' });
  } catch { /* logout failing is fine — local state gets cleared anyway */ }
}

// ===== chat & jobs =====

// SPEC-414 T-b: the legacy sync postChat is gone — every /api/chat turn goes through submitChat + pollTurn
// (async turn mode; the worker's sync branch stays only for old clients until T-c removes it).

// ===== SPEC-392 段B: visual critique (design-review loop) =====

export interface CritiqueIssue {
  desc: string;
  severity: 'hard' | 'soft';
  region?: string;
  /** SPEC-426 M2 A.4 (修复轮): 证据描述 — 截图中具体哪个区域/元素呈现了该问题 */
  evidence?: string;
}

export interface CritiqueResult {
  /** true only when the reviewer found no hard issues */
  pass: boolean;
  issues: CritiqueIssue[];
  /** Raw reviewer output (debug) */
  raw?: string;
  /** SPEC-426 M2 A.4 (修复轮): VL 输出的维度评分（各 0-2 整数）；旧响应无此键 → UI 隐藏
   * 评分表（提示 3 回退：不 crash 不空白） */
  dimensions?: Record<string, number>;
}

/** POST /api/critique — screenshot_url must be a same-origin /api/img/ upload link (worker rejects anything else). */
export async function postCritique(
  instruction: string,
  canvas: CanvasState,
  screenshotUrl: string,
  signal?: AbortSignal,
  /** SPEC-426 M2 A.4 (修复轮): 带参考图 → 评审另评「还原度」维度。省略时请求 body 与旧调用逐字节一致。 */
  hasReference?: boolean
): Promise<CritiqueResult> {
  const resp = await apiFetch('/api/critique', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      instruction, canvas_state: canvas, screenshot_url: screenshotUrl,
      ...(hasReference ? { has_reference: true } : {}),
    }),
    ...(signal ? { signal } : {}),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json as CritiqueResult;
}

// ===== SPEC-402 M3: replicate diff (design-diff loop) =====

export interface DiffIssue {
  desc: string;
  severity: 'hard' | 'soft';
  region?: string;
  /** Canvas object the reviewer tied the issue to (optional) */
  objectId?: string;
}

export interface ReplicateDiffMetrics {
  /** Palette distance vs the reference design spec; null = not comparable (spec missing or no render_palette) */
  paletteDist: number | null;
  /** Share of the reference's texts found on the canvas, 0..1 */
  textRate: number;
  /** Reference texts not found on the canvas */
  missing: string[];
}

export interface ReplicateDiffResult {
  /** true only when no hard issues AND both metrics meet their thresholds */
  pass: boolean;
  issues: DiffIssue[];
  metrics: ReplicateDiffMetrics;
  /** Objective stop signal from the worker (round ≥ 3, or no-hard + both metrics met) — the client never budgets on its own */
  stop: boolean;
  /** Correction instruction for the next round; empty string = no refine round needed */
  refine_instruction: string;
}

/** POST /api/replicate-diff — both image URLs must be same-origin /api/img/ upload links (anything else → 400).
 * Non-200 throws an Error carrying `status` (502 = gateway failure, retryable once by the caller). */
export async function postReplicateDiff(
  args: {
    referenceImage: string;
    screenshotUrl: string;
    canvas: CanvasState;
    projectId?: string;
    refineRound?: number;
    renderPalette?: { hex: string; coverage: number }[];
  },
  signal?: AbortSignal
): Promise<ReplicateDiffResult> {
  const resp = await apiFetch('/api/replicate-diff', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      reference_image: args.referenceImage,
      screenshot_url: args.screenshotUrl,
      canvas_state: args.canvas,
      ...(args.projectId ? { project_id: args.projectId } : {}),
      ...(args.refineRound != null ? { refine_round: args.refineRound } : {}),
      ...(args.renderPalette?.length ? { render_palette: args.renderPalette } : {}),
    }),
    ...(signal ? { signal } : {}),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok) {
    const err = new Error(json?.error ?? `HTTP ${resp.status}`) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return json as ReplicateDiffResult;
}

export async function fetchJob(id: string): Promise<JobRecord> {
  const resp = await apiFetch(`/api/jobs/${id}`);
  const json = await resp.json();
  if (!resp.ok) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json as JobRecord;
}

// ===== SPEC-414 T-b: async agent turns (submit → poll → terminal) =====
// Worker contract (T-a): POST /api/chat {async:true} → 202 {turn_id}; single in-flight turn per user → 409 {active_turn};
// GET /api/turns/:id → TurnRecord; POST /api/turns/:id/cancel; GET /api/turns/active → {turns}
// (newest first; server filters by session user, list items strip `result` — reattach re-polls /turns/:id for it).

/** SPEC-413 M1b: read back the full DesignPlan for a plan card (own-key guarded server-side). */
export async function fetchPlan(key: string): Promise<any | null> {
  const qs = new URLSearchParams({ key });
  const resp = await apiFetch('/api/plan?' + qs.toString());
  if (!resp.ok) return null;
  return resp.json().catch(() => null);
}

export interface SubmitChatExtra {
  /** Required for async turns — the worker rejects a submission without it (400) */
  projectId?: string;
  palette?: { hex: string; coverage: number }[];
  source?: { w: number; h: number };
  /** SPEC-413 M1b: 'mutate' = 重试必带变异 — PLAN 注入变异指令（§6 决策 h） */
  replan?: 'mutate';
}

/**
 * Submit an async agent turn — same payload as the legacy sync postChat plus {async:true}; returns in ~1s
 * with the turn id (the agent itself runs detached in the DO; the caller polls). The SPEC-402 replication
 * measurement chain (projectId/palette/source) rides through untouched.
 * A 409 rejection carries .status and .activeTurn so the caller can prompt "stop the running turn first".
 */
export async function submitChat(
  message: string,
  canvas: CanvasState,
  chat: ChatMsg[],
  needsName: boolean,
  images?: string[],
  extra?: SubmitChatExtra
): Promise<{ turnId: string }> {
  const history = chat.slice(-8).map(m => ({ role: m.role, content: m.text }));
  const resp = await apiFetch('/api/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      message, canvas_state: canvas, history, needs_name: needsName, async: true,
      ...(images?.length ? { images } : {}),
      ...(extra?.projectId ? { project_id: extra.projectId } : {}),
      ...(extra?.palette?.length ? { palette: extra.palette } : {}),
      ...(extra?.source ? { source: extra.source } : {}),
      ...(extra?.replan ? { replan: extra.replan } : {}),
    }),
  });
  const json = await resp.json().catch(() => null);
  if (!resp.ok) {
    const err = new Error(json?.error ?? `HTTP ${resp.status}`) as Error & { status?: number; activeTurn?: string };
    err.status = resp.status;
    if (json?.active_turn) err.activeTurn = String(json.active_turn);
    throw err;
  }
  const turnId = String(json?.turn_id ?? '');
  if (!turnId) throw new Error('submit accepted but no turn_id returned');
  return { turnId };
}

export async function fetchTurn(id: string): Promise<TurnRecord> {
  const resp = await apiFetch(`/api/turns/${id}`);
  const json = await resp.json();
  if (!resp.ok) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json as TurnRecord;
}

/** Ask the worker to stop a turn between agent rounds. queued → cancelled at once; running → 'cancelling' (the final state lands on the next poll). */
export async function cancelTurn(id: string): Promise<{ id: string; status: string }> {
  const resp = await apiFetch(`/api/turns/${id}/cancel`, { method: 'POST' });
  const json = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json as { id: string; status: string };
}

/** Turn list for reload reattach (newest first; the DO returns up to 50 records across users — filter by user/project client-side). */
export async function fetchActiveTurns(): Promise<TurnRecord[]> {
  const resp = await apiFetch('/api/turns/active');
  const json = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return Array.isArray(json?.turns) ? (json.turns as TurnRecord[]) : [];
}

/**
 * Poll a turn to its terminal state (2s interval, pollJob precedent — network blips never kill the poll).
 * Resolves with the terminal TurnRecord; rejects with an AbortError on signal abort, or with err.backstop=true
 * when the 600s frontend budget lapses while the turn is still alive (design §2: ask keep-waiting / cancel — the caller decides).
 */
export function pollTurn(
  id: string,
  opts: { signal?: AbortSignal; onProgress?: (p: TurnProgress) => void; intervalMs?: number; backstopMs?: number } = {}
): Promise<TurnRecord> {
  const { signal, onProgress, intervalMs = 2000, backstopMs = 600_000 } = opts;
  return new Promise<TurnRecord>((resolve, reject) => {
    const deadline = Date.now() + backstopMs;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => { if (timer) clearTimeout(timer); signal?.removeEventListener('abort', onAbort); };
    const fail = (e: unknown) => { cleanup(); reject(e); };
    function onAbort() { fail(new DOMException('Turn polling aborted', 'AbortError')); }
    if (signal?.aborted) { onAbort(); return; }
    signal?.addEventListener('abort', onAbort);
    const tick = async () => {
      try {
        const rec = await fetchTurn(id);
        if (signal?.aborted) return; // the abort listener already rejected
        if (rec.status !== 'queued' && rec.status !== 'running') { cleanup(); resolve(rec); return; }
        onProgress?.(rec.progress);
      } catch { /* keep polling through network blips */ }
      if (Date.now() > deadline) {
        const err = new Error(`turn ${id} still alive past the ${backstopMs / 1000}s poll backstop`) as Error & { backstop?: boolean };
        err.backstop = true;
        fail(err);
        return;
      }
      timer = setTimeout(() => { void tick(); }, intervalMs);
    };
    void tick();
  });
}

export async function retryJob(id: string): Promise<void> {
  const resp = await apiFetch(`/api/jobs/${id}/retry`, { method: 'POST' });
  if (!resp.ok) {
    const json = await resp.json().catch(() => null);
    throw new Error(json?.error ?? `HTTP ${resp.status}`);
  }
}

/** Poll a job until done/error (2s interval, 400s timeout as a backstop — the worker caps a job at 180s × 2 attempts) */
export function pollJob(id: string, onUpdate: (job: JobRecord) => void): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = Date.now() + 400_000;

  const tick = async () => {
    if (stopped) return;
    try {
      const job = await fetchJob(id);
      onUpdate(job);
      if (job.status === 'done' || job.status === 'error') return;
    } catch { /* keep polling through network blips */ }
    if (Date.now() > deadline) return;
    timer = setTimeout(tick, 2000);
  };
  void tick();
  return () => { stopped = true; if (timer) clearTimeout(timer); };
}

// ===== Region-select local editing (crop → edit → paste back) =====

/** Same-origin proxy for remote images (avoids canvas tainting) */
export function proxyImgUrl(u: string): string {
  return `/api/proxy-img?u=${encodeURIComponent(u)}`;
}

/** Upload an image dataURL to KV. ttlDays (optional, SPEC-388) expires transient intermediates like region crops. */
export async function uploadImage(dataUrl: string, ttlDays?: number, signal?: AbortSignal): Promise<string> {
  const resp = await apiFetch('/api/upload', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ data: dataUrl, ...(ttlDays ? { ttl_days: ttlDays } : {}) }),
    ...(signal ? { signal } : {}),
  });
  const json = await resp.json();
  if (!resp.ok || !json?.url) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json.url as string;
}

export async function postRegionEdit(cropUrl: string, instruction: string, transparent = false): Promise<string> {
  const resp = await apiFetch('/api/region-edit', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ crop_url: cropUrl, instruction, transparent }),
  });
  const json = await resp.json();
  if (!resp.ok || !json?.job_id) throw new Error(json?.error ?? `HTTP ${resp.status}`);
  return json.job_id as string;
}

// ===== multi-user registration + admin (SPEC-391) =====

export type RegisterResult = 'ok' | 'pending' | 'bad_username' | 'bad_password' | 'exists' | 'bad_invite' | 'not_configured' | 'network_error';

/** Register (invite flow). 'ok' = active + session cookie already set; 'pending' = awaiting admin approval. */
export async function postRegister(username: string, password: string, invite: string): Promise<RegisterResult> {
  try {
    const resp = await fetch('/api/register', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password, ...(invite.trim() ? { invite: invite.trim() } : {}) }),
    });
    const j = await resp.json().catch(() => null);
    if (resp.ok) return j?.status === 'pending' ? 'pending' : 'ok';
    if (resp.status === 503) return 'not_configured';
    const err = j?.error;
    if (err === 'exists' || err === 'bad_invite' || err === 'bad_username' || err === 'bad_password') return err;
    return 'network_error';
  } catch {
    return 'network_error';
  }
}

export interface AdminUser {
  username: string;
  role: 'learner' | 'admin';
  status: 'pending' | 'active' | 'disabled';
  created_at: string;
  last_login_at: string | null;
}

export async function adminListUsers(): Promise<AdminUser[]> {
  try {
    const resp = await apiFetch('/api/admin/users');
    if (!resp.ok) return [];
    const j = await resp.json();
    return Array.isArray(j?.users) ? j.users : [];
  } catch { return []; }
}

export async function adminCreateInvite(): Promise<string | null> {
  try {
    const resp = await apiFetch('/api/admin/invite', { method: 'POST' });
    if (!resp.ok) return null;
    const j = await resp.json();
    return typeof j?.code === 'string' ? j.code : null;
  } catch { return null; }
}

export async function adminSetUserStatus(username: string, status: AdminUser['status']): Promise<boolean> {
  try {
    const resp = await apiFetch('/api/admin/user-status', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, status }),
    });
    return resp.ok;
  } catch { return false; }
}

export async function adminDeleteUser(username: string): Promise<boolean> {
  try {
    const resp = await apiFetch('/api/admin/user-delete', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    return resp.ok;
  } catch { return false; }
}
